import { createContext, useContext, useMemo, useRef, useSyncExternalStore } from 'react'
import type { Layer } from 'leaflet'
import type { ReactNode } from 'react'

type PoiId = string | number

type LayerRegistry = {
    registerLayer: (poiId: PoiId, geometryIndex: number, layer: Layer) => void
    unregisterLayer: (poiId: PoiId, geometryIndex: number, layer?: Layer) => void
    getLayer: (poiId: PoiId, geometryIndex: number) => Layer | null
    subscribe: (listener: () => void) => () => void
}

const LayerRegistryContext = createContext<LayerRegistry | null>(null)

export const LayerRegistryProvider = ({ children }: { children: ReactNode }) => {
    const layersRef = useRef(new Map<PoiId, Map<number, Layer>>())
    const listenersRef = useRef(new Set<() => void>())

    const registry = useMemo<LayerRegistry>(
        () => ({
            registerLayer: (poiId, geometryIndex, layer) => {
                const poiLayers = layersRef.current.get(poiId) ?? new Map<number, Layer>()

                poiLayers.set(geometryIndex, layer)
                layersRef.current.set(poiId, poiLayers)

                listenersRef.current.forEach((listener) => listener())
            },

            unregisterLayer: (poiId, geometryIndex, layer) => {
                const poiLayers = layersRef.current.get(poiId)

                if (!poiLayers) {
                    return
                }

                // Ignore a stale cleanup callback if a replacement layer is registered.
                if (layer && poiLayers.get(geometryIndex) !== layer) {
                    return
                }

                poiLayers.delete(geometryIndex)

                if (poiLayers.size === 0) {
                    layersRef.current.delete(poiId)
                }

                listenersRef.current.forEach((listener) => listener())
            },

            getLayer: (poiId, geometryIndex) => {
                return layersRef.current.get(poiId)?.get(geometryIndex) ?? null
            },

            subscribe: (listener) => {
                listenersRef.current.add(listener)

                return () => {
                    listenersRef.current.delete(listener)
                }
            },
        }),
        []
    )

    return <LayerRegistryContext.Provider value={registry}>{children}</LayerRegistryContext.Provider>
}

export const useLayerRegistry = (): LayerRegistry => {
    const registry = useContext(LayerRegistryContext)

    if (!registry) {
        throw new Error('useLayerRegistry must be used inside LayerRegistryProvider')
    }

    return registry
}

export const useRegisteredLayer = (poiId: PoiId | null, geometryIndex: number | null): Layer | null => {
    const registry = useLayerRegistry()

    return useSyncExternalStore(
        registry.subscribe,
        () => (poiId !== null && geometryIndex !== null ? registry.getLayer(poiId, geometryIndex) : null),
        () => null
    )
}
